import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faKey } from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MainLayout from '../components/layout/MainLayout';

const ForgotPasswordPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
  });

  // Form handling
  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      
      // In a real implementation, this would call an API
      // For now, we'll just simulate a successful request
      setTimeout(() => {
        setRequestSent(true);
        setIsSubmitting(false);
      }, 1500);
    }
  });

  return (
    <MainLayout>
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={6} md={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-lg-5 p-4">
                <div className="text-center mb-4">
                  <FontAwesomeIcon icon={faKey} size="3x" className="text-primary mb-3" />
                  <h2 className="fw-bold">Forgot Password</h2>
                  <p className="text-muted">Enter your email to reset your password</p>
                </div>
                
                {requestSent ? (
                  <Alert variant="success">
                    <h5 className="alert-heading">Password Reset Email Sent!</h5>
                    <p>
                      If an account with that email exists, we've sent instructions for resetting your password.
                      Please check your inbox and follow the link in the email.
                    </p>
                    <hr />
                    <p className="mb-0">
                      Didn't receive an email? Check your spam folder or <Link to="/forgot-password" onClick={() => setRequestSent(false)}>try again</Link>.
                    </p>
                  </Alert>
                ) : (
                  <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label>Email Address</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FontAwesomeIcon icon={faEnvelope} className="text-muted" />
                        </span>
                        <Form.Control
                          type="email"
                          placeholder="Enter your email"
                          name="email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={formik.touched.email && !!formik.errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.email}
                        </Form.Control.Feedback>
                      </div>
                    </Form.Group>
                    
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="w-100 py-2" 
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Reset Password'}
                    </Button>
                    
                    <div className="text-center mt-4">
                      <p className="mb-0">
                        Remember your password? <Link to="/login" className="text-primary">Sign in</Link>
                      </p>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </MainLayout>
  );
};

export default ForgotPasswordPage;
